import React, { useEffect, useState, type ReactNode } from 'react';
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context';
import { UserManager } from 'oidc-client-ts';
import { setUserManager } from '@/auth/accessToken';
import { ForceLogoutBridge } from '@/auth/ForceLogoutBridge';
import { getDiscoveryUrl } from '@/auth/getDiscoveryUrl';
import { buildOidcConfig } from '@/auth/oidcConfig';
import { setProfileIdentity } from '@/auth/session';
import { STORED_OFFERS_SKIPPED_SESSION } from '@/auth/stored';
import { TokenSync } from '@/auth/TokenSync';
import { clearNewsGalleryImageCache } from '@/utils/newsGalleryImageCache';

interface Props {
  children: ReactNode;
}

const onSigninCallback = async () => {
  await clearNewsGalleryImageCache();
  sessionStorage.removeItem(STORED_OFFERS_SKIPPED_SESSION);
  setProfileIdentity(undefined);
  window.history.replaceState({}, document.title, window.location.pathname);
};

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [userManager, setUserManagerState] = useState<UserManager | null>(null);
  const [initError, setInitError] = useState<Error | null>(null);

  useEffect(() => {
    let active = true;

    const init = async () => {
      try {
        const discoveryUrl = await getDiscoveryUrl();

        if (active) {
          const manager = new UserManager(buildOidcConfig(discoveryUrl));

          setUserManager(manager);
          setUserManagerState(manager);
        }
      } catch (error) {
        if (active) {
          setInitError(error as Error);
        }
      }
    };

    init();

    return () => {
      active = false;
    };
  }, []);

  if (initError) {
    throw initError;
  }

  if (!userManager) {
    return null;
  }

  return (
    <OidcAuthProvider
      userManager={userManager}
      onSigninCallback={onSigninCallback}
    >
      <TokenSync />
      <ForceLogoutBridge>{children}</ForceLogoutBridge>
    </OidcAuthProvider>
  );
};
